// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import InfoCard from "@pace/App/Components/WhyPace/InfoCard";
import * as React from "react";

interface InfoCardFields {
    fields: {
        title: string;
        buttonLabel: string;
        subtitle: string;
        image: string;
        imageCaption: string;
        buttonLink: LinkFieldValue;
    };
}

const enum fields {
    cards = "cards",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.cards]: InfoCardFields[];
    };
}

type Props = OwnProps;

interface InfoCardsWidgetStyles {
    container?: InjectableCss;
    content?: InjectableCss;
}

export const infoCardsWidgetStyles: InfoCardsWidgetStyles = {};

const InfoCardsWidget: React.FC<Props> = ({ fields }) => {
    if (!fields.cards || fields.cards.length === 0) {
        return null;
    }

    const styles = infoCardsWidgetStyles;

    const styledProps = { numberOfCards: fields.cards.length };
    const cards = fields.cards.map((card, index) => (
        <React.Fragment key={index.toString()}>
            <StyledWrapper {...styles.content} {...styledProps}>
                <InfoCard
                    buttonLabel={card.fields.buttonLabel}
                    title={card.fields.title}
                    subtitle={card.fields.subtitle}
                    buttonLink={card.fields.buttonLink}
                    image={card.fields.image}
                    imageCaption={card.fields.imageCaption}
                />
            </StyledWrapper>
        </React.Fragment>
    ));

    return (
        <StyledWrapper {...styles.container} className="whyInfoCardsWrapper">
            {cards}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InfoCardsWidget,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.cards,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title || "Pace Info Card";
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "imageCaption",
                        displayName: "Image Caption",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => item.fields.image,
                    },
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                        maxLength: 40,
                    },
                    {
                        name: "subtitle",
                        displayName: "Card Subtitle",
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLabel",
                        displayName: "Button Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
